import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import API_BASE_URL from '../config/api';

export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  save_history: number; // 1 = сохранять историю, 0 = не сохранять
  balance: number; // Баланс на счету в рублях
}

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  verifyToken: () => Promise<void>;
  updateSettings: (settings: { save_history?: number; balance?: number }) => Promise<void>;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
            username,
            password,
          });

          if (response.data.success) {
            const { token, user } = response.data.data;
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else {
            throw new Error(response.data.error || 'Ошибка авторизации');
          }
        } catch (error: any) {
          let errorMessage: string;
          
          if (!error.response) {
            // Сетевая ошибка
            if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
              errorMessage = 'Не удалось подключиться к серверу. Убедитесь, что сервер запущен на порту 3001';
            } else if (error.code === 'ECONNABORTED') {
              errorMessage = 'Превышено время ожидания ответа от сервера';
            } else {
              errorMessage = `Сетевая ошибка: ${error.message || 'Сервер недоступен'}`;
            }
          } else {
            // Ошибка от сервера
            errorMessage = error.response?.data?.error || error.message || 'Ошибка авторизации';
          }
          
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw new Error(errorMessage);
        }
      },

      // Logout
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
      },

      // Verify token
      verifyToken: async () => {
        const { token } = get();

        if (!token) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        set({ isLoading: true });

        try {
          const response = await axios.get(`${API_BASE_URL}/api/auth/verify`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.data.success) {
            set({
              user: response.data.data.user,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // Token invalid
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          // Token invalid or expired
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      // Update settings
      updateSettings: async (settings) => {
        const { token } = get();

        if (!token) {
          throw new Error('Требуется авторизация');
        }

        set({ isLoading: true, error: null });

        try {
          const response = await axios.patch(
            `${API_BASE_URL}/api/auth/settings`,
            settings,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (response.data.success) {
            set({
              user: response.data.data.user,
              isLoading: false,
            });
          } else {
            throw new Error(response.data.error || 'Ошибка обновления настроек');
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || error.message || 'Ошибка обновления настроек';
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw new Error(errorMessage);
        }
      },

      // Setters
      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

